import {
  type LifecycleConfig,
  parseLifecycleConfig,
} from 'features/libra/lifecycle/lifecycleTypes'
import { OntologyFieldSchema } from 'features/libra/ontology/ontologyFieldTypes'
import {
  parseWorkspaceLayout,
  WorkspaceLayoutConfig,
} from 'features/libra/workspace/workspaceLayoutTypes'
import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import type { Dispatch, SetStateAction } from 'react'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryTagsType, RolodexEntryType } from 'types'
import z from 'zod'

const libraWorkflowStepSchema = z.object({
  id: z.string(),
  name: z.string(),
  component: z.string(),
  config: z.record(z.string(), z.unknown()).default({}),
  workspaceLayout: z.unknown().optional(),
  disableCommenting: z.boolean().default(false),
  /**
   * Declared by the workflow author: this step's content is scoped to one
   * unit component and renders inside per-component tabs
   * (`UnitComponentTabs`). The shell trusts this flag alone -- there is no
   * heuristic inference from config shapes or panel names.
   */
  requiresComponentScope: z.boolean().default(false),
})

export type StepValidationStatus = 'Error' | 'Warning' | 'Success'

export type StepValidationResult =
  | { message: string; status: StepValidationStatus }
  | undefined

export interface LibraWorkflowStepProps<
  Config extends Record<string, unknown>,
> {
  name: string
  config: Config
}

export interface LibraWorflowStepComponentProps<
  Config extends Record<string, unknown>,
> extends LibraWorkflowStepProps<Config> {
  setValidationResult: Dispatch<SetStateAction<StepValidationResult>>
  readonly?: boolean
}

export type UseValidateConfigHook = (
  props: LibraWorkflowStepProps<Record<string, unknown>>
) => boolean

/**
 * A workflow's per-component field definitions live under `ontologyFields.components`
 * (see `ontologyFieldsSchema` below). Shape mirrors the top-level ontology fields.
 */
export const componentOntologySchema = z.record(z.string(), OntologyFieldSchema)

/**
 * Typed shape for `globalContext.ontologyFields`. Reserves the `components` key for the
 * per-component nested map; all other keys are `OntologyField`. A top-level scalar
 * `components` OntologyField cannot sneak through: `componentOntologySchema` is a record
 * of `OntologyField`s, so an OntologyField-shaped value fails its parse. Parsing here
 * (rather than post-hoc) also lands `OntologyFieldSchema`'s `.preprocess()` category
 * default directly on the parsed workflow -- legacy untagged fields arrive as
 * `category: 'input'` with no write-back step.
 */
export const ontologyFieldsSchema = z
  .object({ components: componentOntologySchema.optional() })
  .catchall(OntologyFieldSchema)

// Passthrough preserves legacy per-workflow keys; `ontologyFields` is the one
// key the shell interprets structurally, so it is typed here.
const globalContextSchema = z
  .object({ ontologyFields: ontologyFieldsSchema.optional() })
  .passthrough()

const libraWorkflowSchema = z.object({
  name: z.string(),
  steps: z.array(libraWorkflowStepSchema),
  showTicketProgress: z.boolean().default(true),
  globalContext: globalContextSchema.default({}),
})

export const mapEntryToLibraWorkflow = (entry: GeckoEntry): LibraWorkflow => {
  // One parse validates everything: `globalContextSchema` types
  // `ontologyFields` (rejecting a malformed field-def or a scalar reserved
  // `components` key) while passing legacy keys through.
  const parsed = libraWorkflowSchema.parse(entry.data)
  const steps: LibraWorkflowStep[] = parsed.steps.map((step) => ({
    id: step.id,
    name: step.name,
    component: step.component,
    config: step.config,
    workspaceLayout: parseWorkspaceLayout({
      workspaceLayout: step.workspaceLayout,
    }),
    disableCommenting: step.disableCommenting,
    requiresComponentScope: step.requiresComponentScope,
  }))
  return {
    id: entry.id,
    name: parsed.name,
    steps,
    showTicketProgress: parsed.showTicketProgress,
    globalContext: parsed.globalContext,
    tags: entry.tags,
    stepIndices: new Map(steps.map((step, idx) => [step.id, idx])),
    lifecycleConfig: parseLifecycleConfig(parsed.globalContext),
  }
}

export type LibraWorkflowGlobalContext = z.infer<typeof globalContextSchema>

export interface LibraWorkflow {
  id: string
  name: string
  steps: ReadonlyArray<LibraWorkflowStep>
  tags: RolodexEntryTagsType
  showTicketProgress: boolean
  stepIndices: ReadonlyMap<string, number>
  globalContext: LibraWorkflowGlobalContext
  lifecycleConfig?: LifecycleConfig
}

export interface LibraWorkflowStep {
  id: string
  name: string
  component: string
  config: Record<string, unknown>
  workspaceLayout?: WorkspaceLayoutConfig
  disableCommenting: boolean
  /** See `libraWorkflowStepSchema`: author-declared per-component tabbing. */
  requiresComponentScope: boolean
}

export function useLibraWorkflowSteps({
  ids,
  tags,
}: {
  ids?: ReadonlyArray<string>
  tags?: RolodexEntryTagsType
}): {
  libraWorkflows: ReadonlyArray<LibraWorkflow>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => void
} {
  const queryBody = useMemo<RolodexEntryQueryBody | undefined>(() => {
    const hasTags = tags && !isEmpty(tags)
    const hasIds = ids && !isEmpty(ids)
    if (!hasTags && !hasIds) {
      return undefined
    }

    return {
      type: RolodexEntryType.LIBRA_WORKFLOW,
      ...(hasTags ? { tags } : {}),
      ...(hasIds ? { ids: [...ids] } : {}),
    }
  }, [ids, tags])

  const {
    entries: libraWorkflows,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries(queryBody, {
    swrOptions: {
      compare: isEqual,
      revalidateOnFocus: false,
    },
  })

  return useMemo(
    () => ({
      libraWorkflows: (libraWorkflows ?? []).map(mapEntryToLibraWorkflow),
      isLoading,
      hasEverLoaded,
      forceRefresh,
    }),
    [forceRefresh, hasEverLoaded, isLoading, libraWorkflows]
  )
}
